import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useConnectMeetingDisabled from '../../../../hooks/use-connect-meeting-disabled';
import API, { PrivateMeetingSessionDetailsResponseType, PROTECTED } from '../../../../lib/api';
import { FailureType, JOIN_STATUS, OPEN_MEETING_MESSAGES } from '../../../../lib/constants';
import { EventType } from '../LayoutWrapper';
import { Button, ButtonProps } from '../../../../components/_base/Button';
import Icon from '../../../../components/_base/Icon';
import useMeetingCalendar from '../../../../hooks/use-meeting-calendar';
import { getAddRemoveBtnClassName, getJoinBtnClassName, getTextWidth, showJoinButton } from '../helpers';
import { MediaQueryContextType, useMediaQuery } from '../../../../lib/context-providers/media-query-provider';
import { PassportPageType, ReplaysPageType, SessionsPageType } from '../../../../lib/api/custom-page.types';
import { usePathPrefix } from '../../../../hooks/use-path-prefix';
import { normalizeRoutePath } from '../../../../lib/helpers/urlHelper';
import SubscriptionCalendarButtons from '../../../shared/SubscribeCalendarButton';
import { useCurrentUserInfo } from '../../../../hooks/api/protected/use-current-user-info';
import useEventCalendar from '../../../../hooks/use-event-calendar';
import {
  EventSettingsContextType,
  useEventSettings
} from '../../../../lib/context-providers/event-settings-context/event-settings-context';
import { useOpenMeetingRegistration } from '../../../../hooks/use-open-meeting-registration';
import { isMeetingRegistrationFullAfterLeave } from '../../../../lib/services/open-meeting-registration/open-meeting-registration';
import {
  isAudienceCapacityEnabled,
  isOpenMeetingRegistrationFull,
  OPEN_MEETING_FULL_MESSAGE,
  resolveRegisterJoinStatus,
} from '../../../../lib/helpers/openMeetingRegistrationHelper';
import BookingResultModal from '../../BookAMeetingWrapper/BookingResultModal';
import { RegistrationResultType } from '../../../../hooks/use-booking-subscription';
import './FooterButtons.scss';

export interface IFooterProps {
  meeting: PrivateMeetingSessionDetailsResponseType;
  event: EventType;
  featured?: boolean;
  isCarousel?: boolean;
  withTooltip?: boolean;
  isSessionsTab?: boolean;
  isRegisterBtnShownDown?: boolean;
  isClosestMeeting?: boolean;
  eventToken: string;
  attendeeSubscribed: boolean;
  updateAttendeeSubscription: (subscribed: boolean) => void;
}

const FooterButtons = (props: IFooterProps): JSX.Element => {
  const {
    meeting,
    event,
    isCarousel,
    withTooltip,
    featured = meeting.featured,
    isSessionsTab,
    isClosestMeeting = false,
    eventToken,
    attendeeSubscribed,
    updateAttendeeSubscription,
  } = props;

  const {
    access,
    meetingId,
    joinStatus,
    startDateTime,
    topic,
    audienceCapacityEnabled,
  } = meeting;

  const changeJoinStatus = (newJoinStatus: string) => {
    meeting.joinStatus = newJoinStatus;
    setJoinStatus(newJoinStatus);
  };

  const history = useHistory();
  const pathPrefix = usePathPrefix();
  const [status, setJoinStatus] = useState(joinStatus);
  const [showRegistrationErrorModal, setShowRegistrationErrorModal] = useState(false);
  const [registrationErrorResult, setRegistrationErrorResult] = useState<RegistrationResultType | null>(null);
  const mutationInFlight = useRef(false);
  const { leaveMeeting } = API[PROTECTED];
  const { register, isLoading } = useOpenMeetingRegistration();
  const isCapEnabled = isAudienceCapacityEnabled(audienceCapacityEnabled);
  const isRegistering = isCapEnabled && isLoading;

  useEffect(() => {
    if (mutationInFlight.current) {
      return;
    }
    setJoinStatus(joinStatus);
  }, [joinStatus]);

  const effectiveJoinStatus = resolveRegisterJoinStatus(status);

  const isConnectBtnDisabled = useConnectMeetingDisabled(event, startDateTime);
  const isAddBtnShown = access === 'OPEN';

  const { fetchOutlookCalendarForMeeting, fetchGoogleCalendarForMeeting } = useMeetingCalendar();

  const eventIdString = event.eventId.toString();
  const eventName = event.name || '';

  const { isMobile } = useMediaQuery() as MediaQueryContextType;

  const isRegisterBtnShownDown = props.isRegisterBtnShownDown ?? (!featured || isMobile);
  const eventSettings = useEventSettings() as EventSettingsContextType;
  const sessionsPage = (eventSettings?.customPages || []).find(
    page => page?.type === 'SESSION'
  ) as SessionsPageType | undefined;

  const registerButtonLabel = sessionsPage?.registerButtonLabel || 'Register';
  const subscribeAttendeeToMeetingOnRegistrationEnabled = sessionsPage?.subscribeAttendeeToMeetingOnRegistrationEnabled ?? false;
  const className = 'editable-session';
  const classNameSessionTableCellButtons = `${className}--table-cell-buttons`;
  const classNameSessionTableCellBtnAdd = `${className}--table-cell-btn-add`;
  const classNameSessionTableCellBtnJoin = `${className}--table-cell-btn-join`;

  const calendarSubscribeHandler = useRef<() => Promise<void>>();

  const showRegistrationError = (result: RegistrationResultType) => {
    setRegistrationErrorResult(result);
    setShowRegistrationErrorModal(true);
  };

  const handleRegistrationUnavailable = () => {
    changeJoinStatus(JOIN_STATUS.REGISTRATION_FULL);
    showRegistrationError(RegistrationResultType.UNAVAILABLE);
  };

  const handleRegistrationFailed = () => {
    showRegistrationError(RegistrationResultType.FAILED);
  };

  const toggleGeneralSession = () => {
    const oldJoinStatus = status ?? JOIN_STATUS.NOT_JOINED;

    switch (oldJoinStatus) {
    case JOIN_STATUS.REGISTRATION_FULL:
      return;

    case JOIN_STATUS.NOT_JOINED:
      if (isRegistering || mutationInFlight.current) {
        return;
      }

      mutationInFlight.current = true;

      if (!isCapEnabled) {
        changeJoinStatus(JOIN_STATUS.JOINED_AS_REGISTRANT);
      }

      register(eventIdString, meetingId, { audienceCapacityEnabled })
        .then(async ({ outcome }) => {
          if (outcome === 'registered') {
            if (isCapEnabled) {
              changeJoinStatus(JOIN_STATUS.JOINED_AS_REGISTRANT);
            }

            if (calendarSubscribeHandler.current && subscribeAttendeeToMeetingOnRegistrationEnabled && !attendeeSubscribed) {
              await calendarSubscribeHandler.current();
            }
            return;
          }
          if (outcome === 'unavailable') {
            handleRegistrationUnavailable();
            return;
          }
          if (outcome === 'failed') {
            handleRegistrationFailed();
            return;
          }
          if (!isCapEnabled) {
            changeJoinStatus(oldJoinStatus);
          }
        })
        .catch(() => changeJoinStatus(oldJoinStatus))
        .finally(() => {
          mutationInFlight.current = false;
        });
      return;

    case JOIN_STATUS.JOINED_AS_REGISTRANT:
      if (isRegistering || mutationInFlight.current) {
        return;
      }

      mutationInFlight.current = true;
      changeJoinStatus(JOIN_STATUS.NOT_JOINED);

      leaveMeeting(eventIdString, meetingId)
        .then(async () => {
          if (isCapEnabled && await isMeetingRegistrationFullAfterLeave(eventIdString, meetingId, 'session')) {
            changeJoinStatus(JOIN_STATUS.REGISTRATION_FULL);
          }
        })
        .catch(() => changeJoinStatus(oldJoinStatus))
        .finally(() => {
          mutationInFlight.current = false;
        });
      return;

    default:
      return;
    }
  };

  const getAddRemoveBtnParams = (): ButtonProps => {
    const iconClassName = 'flex-shrink-0';
    const textClassName = 'truncate';
    const style = { lineHeight: isCarousel ? '20px' : '' };
    let labelClassName = 'footer-buttons';
    if (isCarousel) {
      if (!featured || (featured && isMobile)) {
        labelClassName += ' w-2/3';
      }
    } else {
      labelClassName += ' line-height-20px';
    }

    const addLabel =
      <div className={`${labelClassName} footer-buttons__register-label-wrapper`} style={style}>
        <div className='footer-buttons__register-label'>
          <Icon.Add className={`${iconClassName} mr-1`} viewBox='0 0 24 24' />
          <span className={textClassName}>
            {registerButtonLabel}
          </span>
        </div>
      </div>;

    const addedLabel =
      <span className={labelClassName + ' text-off-black'} style={style}>
        <Icon.Added className='mr-1 fill-off-black align-top' viewBox='0 0 24 24' />Registered
      </span>;

    const removeLabel =
      <span className={labelClassName + ' text-white'} style={style}>
        <Icon.Remove className='mr-1 fill-white align-top' viewBox='1 1 18 18' height='14' width='14' />
        <span className='cancel-button'>Cancel</span>
      </span>;

    const addRemoveBtn = effectiveJoinStatus === JOIN_STATUS.NOT_JOINED
      ? addLabel
      : effectiveJoinStatus === JOIN_STATUS.JOINED_AS_REGULAR
        ? addedLabel
        : <>{addedLabel}{removeLabel}</>;

    const addRemoveBtnAriaLabel = effectiveJoinStatus === JOIN_STATUS.NOT_JOINED
      ? registerButtonLabel
      : effectiveJoinStatus === JOIN_STATUS.JOINED_AS_REGULAR
        ? `Registered`
        : `Cancel`;

    const addRemoveBtnParams: ButtonProps = {
      name: 'meetingItem',
      type: 'button',
      onClick: toggleGeneralSession,
      label: addRemoveBtn,
      className: handleGetAddRemoveBtnClassNames(),
      'data-meeting-id': meetingId,
      ariaLabel: addRemoveBtnAriaLabel,
      customRegisterButtonWidth: featured
        ? { width: getTextWidth(registerButtonLabel) ?? '', maxWidth: '200px', minWidth: '112px' }
        : null,
      disabled: isRegistering,
    };

    if (withTooltip && effectiveJoinStatus === JOIN_STATUS.NOT_JOINED) {
      addRemoveBtnParams['data-tooltip'] = registerButtonLabel;
      addRemoveBtnParams['data-tooltip-position'] = 'bottom';
    }
    return addRemoveBtnParams;
  };

  const handleGetJoinBtnClassNames = () => getJoinBtnClassName(
    classNameSessionTableCellBtnJoin,
    !!isMobile,
    featured,
    isConnectBtnDisabled,
    access === 'PLENARY',
    (!isSessionsTab && !isClosestMeeting),
    isSessionsTab
  );

  const handleGetAddRemoveBtnClassNames = () => getAddRemoveBtnClassName(
    classNameSessionTableCellBtnAdd,
    !!isMobile,
    effectiveJoinStatus ?? '',
    featured,
  );

  const AddToCalendarButton = (props: { isFeatured?: boolean }) => {
    const { isFeatured = false } = props;
    const [customStyles, setCustomStyles] = useState<React.CSSProperties>({});
    const { data: currentUserInfo } = useCurrentUserInfo();
    const { unSubscribeToCalendarEvent } = useEventCalendar();

    const getOrganizationName = (): string => {
      return (meeting.presenterOrganizations?.length)
        ? meeting.presenterOrganizations.map(o => o.name).join(', ')
        : '';
    };

    const onShowDropDown = (buttonRef: RefObject<HTMLDivElement>) => {
      const buttonLeftPosition = buttonRef.current?.getBoundingClientRect().x || 0;

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (buttonLeftPosition > document.body.clientWidth / 2) {
        setCustomStyles({ left: '-214px' });
      } else {
        setCustomStyles({});
      }
    };

    return (
      access === 'PLENARY' ||
      (effectiveJoinStatus !== JOIN_STATUS.NOT_JOINED &&
        effectiveJoinStatus !== JOIN_STATUS.REGISTRATION_FULL)
    )
      ? (
        <SubscriptionCalendarButtons
          customStyles={!isFeatured ? customStyles : {}}
          onShowDropDown={onShowDropDown}
          eventId={event.eventId.toString()}
          name={getOrganizationName()}
          attendeeSubscribed={attendeeSubscribed}
          calendarInvite={event.calendarInvite}
          data={currentUserInfo}
          className={'editable-welcome--subscribe-title'}
          eventTitleForPassport={topic}
          downloadIcsFile={
            () => fetchOutlookCalendarForMeeting(
              eventIdString,
              meetingId,
              `${event.eventTitleForPassport || eventName}.ics`,
              eventToken
            )
          }
          openGoogleCalendar={() => fetchGoogleCalendarForMeeting(eventIdString, meetingId, eventToken)}
          iconButton
          unSubscribeToCalendar={(resgistrationId: string) => unSubscribeToCalendarEvent(resgistrationId)}
          subscribeButtonLabel="Add to Calendar"
          subscribedButtonLabel="Added to Calendar"
          isSubscribeToEvent
          updateSubscribe={updateAttendeeSubscription}
          meetingId={meetingId}
          meetingEndDateTime= {meeting.endDateTime}
          isSlider
          triggerSubscription={(fn) => {
            calendarSubscribeHandler.current = fn;
          }}
        />
      )
      : null
    ;
  };

  const JoinButton = ({ preRegistrationEnabled }: { preRegistrationEnabled: boolean }) => showJoinButton(access, preRegistrationEnabled, effectiveJoinStatus)
    ? (
      <Button
        name='meetingItem'
        data-meeting-id={meetingId}
        type='button'
        onClick={() => history.push(normalizeRoutePath(`/session/${event.eventId}/meeting/${meetingId}`, pathPrefix), history.location.pathname)}
        label={isConnectBtnDisabled ? 'View Details' : 'Join'}
        className={`${handleGetJoinBtnClassNames()}${isCarousel ? ' py-2' : ''}`}
        ariaLabel={isConnectBtnDisabled ? 'View Details' : 'Join Meeting'}
        data-test-id='join-button'
      />
    )
    : null;

  const MeetingFullLabel = () => {
    const style = { lineHeight: isCarousel ? '20px' : '' };
    let labelClassName = 'footer-buttons meeting-full-message';
    if (isCarousel) {
      if (!featured || (featured && isMobile)) {
        labelClassName += ' w-2/3';
      }
    } else {
      labelClassName += ' line-height-20px';
    }

    let fullMessageClassName = `${classNameSessionTableCellBtnAdd} font-size-11px font-semibold add-remove-btn justify-center meeting-full-message ${labelClassName}`;
    if (isMobile) {
      fullMessageClassName += ' border-t p-2 bg-cool-gray';
    } else {
      fullMessageClassName += ' p-10px ';
      if (!featured) {
        fullMessageClassName += ' border-t bg-cool-gray h-44px';
      } else {
        fullMessageClassName += ' w-auto rounded m-right-15px h-34px';
      }
    }

    return (
      <div
        className={fullMessageClassName}
        data-test-id='sessions-meeting-full-message'
        style={style}
        role='status'
        aria-live='polite'
      >
        {OPEN_MEETING_FULL_MESSAGE}
      </div>
    );
  };

  const AddButton = () => {
    if (!isAddBtnShown) {
      return isCarousel
        ? <div className={`${!featured ? 'bg-cool-gray' : ''} carousel-button`} />
        : null;
    }

    if (isOpenMeetingRegistrationFull(status)) {
      return <MeetingFullLabel />;
    }

    if (isRegistering) {
      const style = { lineHeight: isCarousel ? '20px' : '' };
      let labelClassName = 'footer-buttons';
      if (isCarousel) {
        if (!featured || (featured && isMobile)) {
          labelClassName += ' w-2/3';
        }
      } else {
        labelClassName += ' line-height-20px';
      }

      const loadingLabel =
        <div
          className={`${labelClassName} footer-buttons footer-buttons__register-label-wrapper footer-buttons__register-label`}
          style={style}
        >
          <span className='preview-spinner add-spin animate-spin' />
          <label className='font-size-11px loading-label'>{OPEN_MEETING_MESSAGES.REGISTERING}</label>
        </div>;

      return (
        <Button
          name='meetingItem'
          type='button'
          label={loadingLabel}
          className={`${handleGetAddRemoveBtnClassNames()} footer-buttons footer-buttons__loading`}
          disabled
          ariaLabel='Registering'
          data-test-id='sessions-register-loading'
        />
      );
    }

    return <Button {...getAddRemoveBtnParams()} />;
  };

  let preRegistrationEnabled = event.preRegistrationEnabled;
  if (!isSessionsTab) {
    const pageConfiguration = event.customPages.find(el => el.type === PassportPageType.REPLAYS) as ReplaysPageType;
    preRegistrationEnabled = pageConfiguration.replaysPreRegistrationEnabled ;
  }

  return (
    <>
      <div className={`footer-buttons-wrapper flex ${featured ? 'items-center' : ''} ${classNameSessionTableCellButtons} ${isRegisterBtnShownDown ? 'flex-col-reverse' : ''}`}>
        <AddButton />
        <div className={` ${featured ? 'h-44px' : ''} session-meeting__content flex items-center`}>
          <JoinButton preRegistrationEnabled={preRegistrationEnabled}/>
          <AddToCalendarButton isFeatured={featured} />
        </div>
      </div>
      <BookingResultModal
        isOpen={showRegistrationErrorModal}
        onClose={() => {
          setShowRegistrationErrorModal(false);
          setRegistrationErrorResult(null);
        }}
        slot={null}
        speakers={[]}
        showLocalTime={false}
        eventTimeZone={event.timeZone || ''}
        result={registrationErrorResult ?? RegistrationResultType.FAILED}
        failureType={
          registrationErrorResult === RegistrationResultType.UNAVAILABLE
            ? FailureType.OPEN_MEETING_FULL
            : undefined
        }
      />
    </>
  );
};

export { FooterButtons };
